import React, { useCallback } from 'react';
import { Dropdown, Option, Scroller, X } from '../styled';
import { AutocompleteStyles } from '../types';
import { Portal } from './Portal';

interface DropdownProps {
  isOpen: boolean;
  filteredData: string[];
  value?: string[];
  onSelectOption: (option: string) => void;
  focusedIndex?: number;
  dropdownId: string;
  styles?: AutocompleteStyles;
  small?: boolean;
  usePortal?: boolean;
  anchorElement?: React.RefObject<HTMLElement>;
}

export const DropdownComponent: React.FC<DropdownProps> = React.memo(
  ({
    isOpen,
    filteredData,
    value,
    onSelectOption,
    focusedIndex = -1,
    dropdownId,
    styles,
    small,
    usePortal = false,
    anchorElement,
  }) => {
    const createOptionClickHandler = useCallback(
      (option: string) => {
        return () => onSelectOption(option);
      },
      [onSelectOption],
    );

    if (!isOpen) return null;

    const dropdownContent = (
      <Dropdown
        style={{
          ...styles?.dropdown,
          ...(usePortal && {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            marginTop: '0px',
          }),
        }}
        id={dropdownId}
        small={small}
        role="listbox"
        aria-label="Options"
      >
        <Scroller>
          {filteredData.map((option, index) => {
            const isSelected = value?.includes(option) ?? false;
            const isFocused = index === focusedIndex;
            return (
              <Option
                key={`${option}`}
                id={dropdownId ? `${dropdownId}-option-${index}` : undefined}
                isFocused={isFocused}
                isSelected={isSelected}
                onClick={createOptionClickHandler(option)}
                role="option"
                aria-selected={isSelected}
                style={styles?.option}
              >
                {option}
                {isSelected && <X>✕</X>}
              </Option>
            );
          })}
        </Scroller>
      </Dropdown>
    );

    if (usePortal && anchorElement) {
      return (
        <Portal isOpen={isOpen} anchorElement={anchorElement}>
          {dropdownContent}
        </Portal>
      );
    }

    return dropdownContent;
  },
);
